/**
 * Uninest/Karmabooking
 *
 * Copyright (c) 2020 Karmabunny
 */

import Vue from 'vue';
import Vuex, { MutationTree, ActionTree, GetterTree } from 'vuex';
import VuexPersistence from 'vuex-persist';
import API, { RoomTypeJson, PropertyJson, BookingJson, SearchResultJson } from '@/api';
import { DateTime } from 'luxon';

Vue.use(Vuex);

interface SearchModel {
    start_date: Date;
    duration: number;
    gender: string;
    property_ref: string | undefined;
}

interface PersonalModel {
    // part 1
    passport_first_name: string;
    passport_last_name: string;
    native_first_name: string;
    native_last_name: string;
    preferred_name: string;

    // part 2
    date_of_birth?: Date;
    nationality: string;
    email_address: string;
    mobile_number: string;
    address: string;
    languages: string[];

    // part 3
    prefer_bed_type: string;
    prefer_floor: string;
    prefer_languages: string[];

    // part 4
    marketing_source: string;
    marketing_action: string;
    marketing_notes: string;

    // part 5
    emergency_contact_name: string;
    emergency_contact_phone: string;
    emergency_contact_email: string;
    emergency_contact_address: string;
    emergency_contact_relationship: string;

    // part 6 (ish)
    terms_agreed: boolean;
}

interface State extends Partial<SearchModel>, PersonalModel {
    // UI states
    searching: boolean;
    dirty_search: boolean;
    disable_transitions: boolean;

    // cache
    properties: PropertyJson[];
    rooms: RoomTypeJson[];

    // more booking stuff
    room_id: number | undefined;
    room_type_ref: string | undefined;
    booking_uid: string | undefined;
    payment_uid: string | undefined;

    booking_confirm: boolean,
    personal_confirm: boolean,
    complete: boolean,
}

// We must also define 'undefined' because the observers break without it.
const state: State = {
    searching: false,
    dirty_search: true,
    disable_transitions: false,

    properties: [],
    rooms: [],

    // location / search
    property_ref: undefined,
    start_date: undefined,
    duration: undefined,
    gender: undefined,

    // room-type
    room_id: undefined,
    room_type_ref: undefined,

    // booking
    booking_uid: undefined,
    booking_confirm: false,

    // personal
    personal_confirm: false,

    passport_first_name: "",
    passport_last_name: "",
    native_first_name: "",
    native_last_name: "",
    preferred_name: "",

    date_of_birth: undefined,
    nationality: "",
    email_address: "",
    mobile_number: "",
    address: "",
    languages: [],

    prefer_bed_type: "",
    prefer_floor: "",
    prefer_languages: [],

    marketing_source: "",
    marketing_action: "",
    marketing_notes: "",

    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_email: "",
    emergency_contact_address: "",
    emergency_contact_relationship: "",

    terms_agreed: false,

    // payment
    complete: false,
    payment_uid: undefined,
    // TODO
};

const mutations: MutationTree<State> = {
    // Store the property set.
    setProperties(state, properties: PropertyJson[]) {
        state.properties = properties;
    },

    // Store the room set.
    setRooms(state, rooms: RoomTypeJson[]) {
        state.rooms = rooms;
        state.dirty_search = false;

        // If the room id is already set, we need to ensure that it belongs
        // to the current room set.
        if (state.room_id) {
            // TODO This assumes rooms have a globally unique ID.
            // Could properties have the same room_id but are actually different rooms?
            const room = state.rooms.find(room => room.room_id === state.room_id);

            // Select the first room if we've got new set of rooms.
            if (!room) {
                state.room_id = state.rooms?.[0].room_id;
            }
        }
    },

    // Global state for searching.
    // Attach this to button/inputs 'disabled' props.
    blockSearching(state, searching: boolean) {
        state.searching = searching;
    },

    // Select a property.
    // Unset room_id/confirmation because we will have a new set of room ids
    // for the new property.
    setProperty(state, property_ref: string) {
        Object.assign(state, {
            property_ref,
            room_id: undefined,
            dirty_search: true,
            booking_confirm: false,
        })
    },

    // Select a room.
    setRoom(state, value: {room_uid: string, room_type_ref: string}) {
        Object.assign(state, value);
    },

    confirmBooking(state) {
        state.booking_confirm = true;
    },

    updatePersonal(state, personal: Partial<PersonalModel>) {
        Object.assign(state, personal);
    },

    confirmPersonal(state) {
        state.personal_confirm = true;
    },

    updateSearch(state, search: Partial<SearchModel>) {
        search.property_ref = state.property_ref;
        Object.assign(state, search);
        state.dirty_search = true;
    },

    confirmPayment(state) {
        state.complete = true;
    },

    disableTransitions(state, toggle: boolean) {
        state.disable_transitions = toggle;
    },

    setBooking(state, booking: string | BookingJson) {
        if (typeof booking === 'string') {
            state.booking_uid = booking;
        }
        else {
            state.booking_uid = booking.booking_uid;
            state.room_id = booking.room_id;
            state.room_type_ref = booking.room_type_ref;
            state.property_ref = booking.property_ref;

            if (booking.start_date) {
                const start_date = DateTime.fromFormat(booking.start_date, 'yyyy-MM-dd');
                state.start_date = start_date.toJSDate();

                if (booking.end_date) {
                    const end_date = DateTime.fromFormat(booking.end_date, 'yyyy-MM-dd')
                    state.duration = Math.ceil(end_date.diff(start_date).as('weeks'));
                }
            }

            state.gender = booking.gender;
            state.passport_first_name = booking.passport_first_name || '';
            state.passport_last_name = booking.passport_last_name || '';
            state.native_first_name = booking.native_first_name || '';
            state.native_last_name = booking.native_last_name || '';

            state.preferred_name = booking.preferred_name || '';
            state.date_of_birth = booking.date_of_birth
                ? DateTime.fromISO(booking.date_of_birth).toJSDate()
                : undefined;

            state.nationality = booking.nationality || '';
            state.email_address = booking.email_address || '';
            state.mobile_number = booking.mobile_number || '';
            state.languages = booking.languages || [];
            state.prefer_bed_type = booking.prefer_bed_type || '';
            state.prefer_floor = booking.prefer_floor || '';
            state.prefer_languages = booking.prefer_languages || [];
            state.marketing_source = booking.marketing_source || '';
            state.marketing_action = booking.marketing_action || '';
            state.marketing_notes = booking.marketing_notes || '';

            state.emergency_contact_name = booking.emergency_contact_name || '';
            state.emergency_contact_phone = booking.emergency_contact_phone || '';
            state.emergency_contact_email = booking.emergency_contact_email || '';
            state.emergency_contact_address = booking.emergency_contact_address || '';
            state.emergency_contact_relationship = booking.emergency_contact_relationship || '';

            state.booking_confirm = !!state.passport_first_name;
            state.complete =
                booking.payment_status === "processed" ||
                booking.status === "complete";
        }
    },
};

const actions: ActionTree<State, State> = {
    async search({ commit, state }) {
        // TODO doesn't work on refresh.
        // if (state.searching) return;

        const { property_ref, start_date, duration, gender } = state;

        if (property_ref && start_date && duration) {

            const start_date_str =
                DateTime.fromJSDate(start_date)
                .toISODate();

            const end_date_str =
                DateTime.fromJSDate(start_date)
                .plus({ weeks: duration })
                .minus({ days: 1 })
                .toISODate();

            try {
                commit('blockSearching', true);

                const rooms = await API.getSearch(
                    property_ref,
                    start_date_str!,
                    end_date_str!,
                    gender!,
                );

                commit('setRooms', rooms);
            }
            finally {
                commit('blockSearching', false);
            }
        }
    },

    async refresh({ commit, dispatch }) {

        // TODO These don't need to be synchronous.

        commit('blockSearching', true);
        commit('setProperties', await API.getProperties());
        await dispatch('search');
    },

    async loadBooking({ state, commit }, booking_uid: string) {
        try {
            const booking = await API.getBooking(booking_uid);
            commit('setBooking', booking);
        }
        catch (error: any) {
            console.log(error);
        }
    },

    async saveBooking({ state, commit }) {
        // TODO Rename this to api_busy.
        if (state.searching) return;

        let { booking_uid } = state;

        if (!booking_uid) {
            booking_uid = await API.createBooking();
            commit('setBooking', booking_uid);
        }
        const start_date_str =
            state.start_date
            ? DateTime.fromJSDate(state.start_date)
                .toISODate()
            : undefined;

        const end_date_str =
            state.start_date && state.duration
            ? DateTime.fromJSDate(state.start_date)
                .plus({ weeks: state.duration })
                .minus({ days: 1 })
                .toISODate()
            : undefined;

        const date_of_birth_str =
            state.date_of_birth
            ? DateTime.fromJSDate(state.date_of_birth).toISODate()
            : undefined;

        await API.editBooking(booking_uid!, {
            start_date: start_date_str!,
            end_date: end_date_str!,
            gender: state.gender,
            room_id: state.room_id,
            property_ref: state.property_ref,
            passport_first_name: state.passport_first_name,
            passport_last_name: state.passport_last_name,
            native_first_name: state.native_first_name,
            native_last_name: state.native_last_name,
            preferred_name: state.preferred_name,
            date_of_birth: date_of_birth_str!,
            nationality: state.nationality,
            email_address: state.email_address,
            mobile_number: state.mobile_number,
            address: state.address,
            languages: state.languages,
            prefer_bed_type: state.prefer_bed_type,
            prefer_floor: state.prefer_floor,
            prefer_languages: state.prefer_languages,
            marketing_source: state.marketing_source,
            marketing_action: state.marketing_action,
            marketing_notes: state.marketing_notes,
            emergency_contact_name: state.emergency_contact_name,
            emergency_contact_phone: state.emergency_contact_phone,
            emergency_contact_email: state.emergency_contact_email,
            emergency_contact_address: state.emergency_contact_address,
            emergency_contact_relationship: state.emergency_contact_relationship,
            terms_agreed: state.terms_agreed,
        });
    },
};

const getters: GetterTree<State, State> = {
    property: state => {
        return state.property_ref && state.properties.find(property => (
            property.property_ref === state.property_ref
        ));
    },
    room: state => {
        return state.room_id && state.rooms.find(room => (
            room.room_id === state.room_id
        ));
    },
    isLocationValid: state => {
        return !!state.property_ref &&
            !!state.start_date &&
            !!state.duration
    },

    isRoomValid: state => {
        return !!state.room_id;
    },

    isBookingComplete: state => {
        return state.booking_confirm;
    },

    isPersonalValid: state => {
        return (
            !!state.passport_first_name &&
            !!state.passport_last_name &&
            !!state.date_of_birth &&
            !!state.email_address &&
            !!state.address &&
            state.languages.length !== 0 &&
            !!state.terms_agreed
        );
    },

    isPersonalComplete: (state, getters) => {
        return (
            state.personal_confirm &&
            getters.isPersonalValid
        );
    },

    isComplete: state => {
        return state.complete;
    },
}

const persist = new VuexPersistence<State>({
    storage: window.sessionStorage,
    restoreState: (key, storage) => {
        const state = storage?.getItem(key);
        if (!state) return {};

        return JSON.parse(state, (key, value) => {
            // Parse date times.
            if (/\d+-\d+-\d+T\d+:\d+:\d+\.\d+Z/.test(value)) {
                return DateTime.fromISO(value).toJSDate();
            }
            // Force searching to false on hydrate.
            else if (key === 'searching' || key === 'dirty_search') {
                return false;
            }
            return value;
        });
    }
});

const store = new Vuex.Store<State>({
    strict: process.env.NODE_ENV !== 'production',
    state,
    mutations,
    actions,
    getters,
    plugins: [persist.plugin],
});

export default store;
